import React, { useState } from 'react';
import styles from './DocumentListVault.module.scss';
import { Box, TableCell, TableRow, Tooltip, Typography } from '@mui/material';
import { Helpers } from '../../../../common/helpers/helpers';
import { useDocumentsFunctions } from '../../hooks/useDocumentsFunctions';
import EmployeeIcon from '@mui/icons-material/BadgeOutlined';
import BusinessCenterOutlinedIcon from '@mui/icons-material/BusinessCenterOutlined';
import CustomersIcon from '@mui/icons-material/SupportAgentOutlined';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import { DocumentsListRowProps } from './types';
import { useTranslation } from 'react-i18next';
import { ROUTES } from '../../../../common/constants/routes';
import { useLocation } from 'react-router-dom';
import { sanitizeRedirectUrl } from '../../../../common/helpers/sanitize';
import { useDocumensoRedirect } from '../../../../common/hooks/useDocumensoRedirect';

/** A document added less than 5 minutes ago gets the "New" badge (same rule as the full row). */
const NEW_DOCUMENT_PERIOD = 300000;

/** File type badge colours: PDF red, Word blue, images green, Business Kitz purple. */
const FILE_TYPE_VARIANTS: Record<string, string> = {
  pdf: styles.file_badge_pdf,
  doc: styles.file_badge_word,
  docx: styles.file_badge_word,
  png: styles.file_badge_image,
  jpg: styles.file_badge_image,
  jpeg: styles.file_badge_image,
  heic: styles.file_badge_image,
  bk: styles.file_badge_bk,
};

const IMAGE_FILE_TYPES = ['png', 'jpg', 'jpeg', 'heic'];

/**
 * Document Vault page row (mockup layout): file type badge, then the document name with the
 * assignee and the workflow underneath, the dates, and the action buttons. The full multi-column
 * row (DocumentsListRow) is still used by the profile pages.
 */
export const DocumentsListRowCompact = ({ doc, renderActionButtons }: DocumentsListRowProps) => {
  const { t } = useTranslation();
  const location = useLocation();
  const { redirectToDocumenso } = useDocumensoRedirect();
  const path = location.pathname;
  const {
    getWorkflow,
    getFileTypeFromName,
    getProfileName,
    getProfileType,
    navigateToDocumentPageTab,
  } = useDocumentsFunctions();

  const renderProfileIcon = (profileType: string) => {
    switch (profileType) {
      case 'employment':
        return <EmployeeIcon fontSize='inherit' />;
      case 'supplier':
        return <BusinessCenterOutlinedIcon fontSize='inherit' />;
      case 'customer':
        return <CustomersIcon fontSize='inherit' />;
      case 'business':
        return <ApartmentOutlinedIcon fontSize='inherit' />;
      default:
        return null;
    }
  };

  /** Shown instead of the assignee name when nobody is assigned (as in the mockup). */
  const getProfileTypeLabel = (profileType: string) => {
    switch (profileType) {
      case 'employment':
        return t('labels.employee');
      case 'supplier':
        return t('labels.supplier');
      case 'customer':
        return t('labels.customer');
      case 'business':
        return t('tagsLabels.categories.business');
      default:
        return '';
    }
  };

  const getFileTypeTooltip = (fileType: string) => {
    if (fileType === 'pdf') return t('fileTypes.pdf');
    if (fileType === 'doc' || fileType === 'docx') return t('fileTypes.msWord');
    if (fileType === 'bk') return t('fileTypes.businessKitz');
    if (IMAGE_FILE_TYPES.includes(fileType)) return t('fileTypes.image');
    return fileType.toUpperCase();
  };

  const profileType = getProfileType(doc.category);
  const profileIcon = renderProfileIcon(profileType);
  const assignedNames = getProfileName(profileType, doc).filter((name) => name && name !== '-');
  const profileText =
    assignedNames.length > 0 ? assignedNames.join(', ') : getProfileTypeLabel(profileType);
  const workflow = getWorkflow(doc);
  const fileType = (
    'du_filetype' in doc
      ? doc.du_filetype && doc.du_filetype.length > 0
        ? doc.du_filetype[0]
        : getFileTypeFromName(doc.name, doc.contract_generation)
      : 'bk'
  )?.toLowerCase();
  const [now] = useState(() => Date.now());
  const isNew = now - new Date(doc.created ?? 0).getTime() < NEW_DOCUMENT_PERIOD;
  const isSigned =
    doc.status === 'COMPLETED' ||
    Boolean(
      doc.extras &&
      doc.extras.length > 0 &&
      typeof doc.extras[0].is_signed === 'boolean' &&
      doc.extras[0].is_signed
    );
  const documentName =
    doc.name.endsWith('.pdf') || doc.name.endsWith('.docx') || doc.name.endsWith('.doc')
      ? doc.name.replace('.pdf', '').replace('.docx', '').replace('.doc', '')
      : doc.name;

  const navigateToDocument = () => {
    if (doc.signing_doc_id) {
      redirectToDocumenso(
        `${ROUTES.DOCUMENTS}/${doc.signing_doc_id}?view=true&backUrl=${path}&activeTab=${doc.category}`
      ).catch();
    } else {
      navigateToDocumentPageTab(doc, 0);
    }
  };

  /** Signed / Pending labels open the signing document; other clicks open the document. */
  const handleStatusClick = doc.signing_doc_id
    ? (event: React.MouseEvent) => {
        event.stopPropagation();
        redirectToDocumenso(
          `${ROUTES.DOCUMENTS}/${doc.signing_doc_id}?view=true&backUrl=${encodeURIComponent(sanitizeRedirectUrl(path))}&activeTab=${encodeURIComponent(doc.category)}`
        );
      }
    : undefined;

  return (
    <TableRow
      className={`${styles.table_row} ${styles.compact_row} ${isNew ? styles.doc_new : ''}`}
    >
      <TableCell
        id={`documentVaultDocumentsListRowNavigateToDocumentCell_${doc?.id}`}
        className={`${styles.table_cell} ${styles.compact_name_cell}`}
        onClick={() => navigateToDocument()}
      >
        <Box
          id={`documentVaultDocumentsListRowNavigateToDocumentCellBox_${doc?.id}`}
          className={styles.compact_name_container}
        >
          {fileType && (
            <Tooltip title={getFileTypeTooltip(fileType)} arrow placement='top'>
              <Box
                id={`documentVaultDocumentsListRowFileTypeBadge_${doc?.id}`}
                className={`${styles.file_badge} ${FILE_TYPE_VARIANTS[fileType] ?? ''}`}
              >
                {fileType}
              </Box>
            </Tooltip>
          )}
          <Box className={styles.compact_text_container}>
            <Box className={styles.compact_title_line}>
              <Typography
                id={`documentVaultDocumentsListRowNavigateToDocumentCellBox3_${doc?.id}`}
                component='span'
                className={styles.compact_title}
              >
                {documentName}
              </Typography>
              {isNew && (
                <Box
                  id={`documentVaultDocumentsListRowNavigateToDocumentCellBox4_${doc?.id}`}
                  className={styles.badge_new}
                >
                  {t('labels.new')}
                </Box>
              )}
              {isSigned ? (
                <Tooltip
                  title={
                    doc.signing_doc_id
                      ? t('messages.signingSignedStatus')
                      : t('messages.markedAsSigned')
                  }
                  arrow
                  placement='top'
                >
                  <Box
                    id={`documentVaultDocumentsListRowSignedArea_${doc?.id}`}
                    className={`${styles.green_label} ${styles.compact_status}`}
                    tabIndex={-1}
                    onClick={handleStatusClick}
                  >
                    {t('labels.signed')}
                  </Box>
                </Tooltip>
              ) : doc.status === 'PENDING' ? (
                <Tooltip title={t('messages.signingPendingStatus')} arrow placement='top'>
                  <Box
                    id={`documentVaultDocumentsListRowPendingArea_${doc?.id}`}
                    className={`${styles.blue_label} ${styles.compact_status}`}
                    tabIndex={-1}
                    onClick={handleStatusClick}
                  >
                    {t('labels.pending')}
                  </Box>
                </Tooltip>
              ) : null}
            </Box>
            {(profileText || workflow !== '-') && (
              <Typography
                id={`documentVaultDocumentsListRowSubtitle_${doc?.id}`}
                component='span'
                className={styles.compact_subtitle}
              >
                {profileText && (
                  <span
                    id={`documentVaultDocumentsListRowNavigateToDocumentCell2Text_${doc?.id}`}
                    className={styles.compact_profile}
                  >
                    {profileIcon && (
                      <span className={styles.compact_profile_icon} aria-hidden='true'>
                        {profileIcon}
                      </span>
                    )}
                    {profileText}
                  </span>
                )}
                {profileText && workflow !== '-' && (
                  <span className={styles.compact_separator} aria-hidden='true'>
                    &middot;
                  </span>
                )}
                {workflow !== '-' && (
                  <span id={`documentVaultDocumentsListRowNavigateToDocumentCell3Text_${doc?.id}`}>
                    {workflow}
                  </span>
                )}
              </Typography>
            )}
          </Box>
        </Box>
      </TableCell>
      <TableCell
        id={`documentVaultDocumentsListRowNavigateToDocumentCell4_${doc?.id}`}
        className={`${styles.table_cell} ${styles.compact_dates_cell}`}
        onClick={() => navigateToDocument()}
      >
        <Box className={styles.compact_dates}>
          <Tooltip title={t('documentsTable.date')} arrow placement='top'>
            <Typography
              id={`documentVaultDocumentsListRowNavigateToDocumentCell4Text_${doc?.id}`}
              component='span'
              className={styles.compact_date}
            >
              <span className={styles.compact_date_label}>{t('documentsTable.updated')}</span>{' '}
              {Helpers.timeStampToDateV2(doc.updated)}
            </Typography>
          </Tooltip>
          {doc.key_date_end_date && (
            <Tooltip title={t('labels.endRenewalDate')} arrow placement='top'>
              <Typography
                id={`documentVaultDocumentsListRowNavigateToDocumentCell5Text_${doc?.id}`}
                component='span'
                className={`${styles.compact_date} ${styles.compact_date_end}`}
              >
                <span className={styles.compact_date_label}>{t('documentsTable.endsRenews')}</span>{' '}
                {Helpers.timeStampToDateV2(doc.key_date_end_date)}
              </Typography>
            </Tooltip>
          )}
        </Box>
      </TableCell>
      <TableCell
        id={`documentVaultDocumentsListRowRedirectToDocumensoCell_${doc?.id}`}
        className={`${styles.table_cell} ${styles.compact_actions_cell}`}
      >
        {renderActionButtons(doc)}
      </TableCell>
    </TableRow>
  );
};
